import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  GripVertical,
} from 'lucide-react';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import type { ResultRow } from '../../reporting/arrowToRows';
import { seriesKeysAdditive } from '../../reporting/chartFormat';
import { DETAIL_ROW_LIMIT } from '../../reporting/compileReport';
import {
  type DimensionFormat,
  type FormatKind,
  formatValue,
  humanizeHeader,
  isNumericKind,
  resolveFormatKind,
} from '../../reporting/formatValue';
import type { DatasetColumn, DateGranularity } from '../../types/reporting';

const PAGE_SIZE_OPTIONS = [50, 100, 250] as const;
const DEFAULT_PAGE_SIZE = 50;

// Shared summation for the TOTAL row and per-group subtotal rows: sum each
// additive (count/sum) measure column over `subset`, leaving dimensions and
// non-additive (avg/min/max) measures null — summing those is meaningless.
// Null when no numeric value was seen so the cell renders blank, not 0.
const sumAdditiveColumns = (
  subset: ResultRow[],
  columns: string[],
  dimensionSet: Set<string>
): Record<string, number | null> => {
  const sums: Record<string, number | null> = {};
  for (const column of columns) {
    if (dimensionSet.has(column) || !seriesKeysAdditive([column])) {
      sums[column] = null;
      continue;
    }
    let total = 0;
    let sawNumber = false;
    for (const row of subset) {
      const value = row[column];
      if (typeof value === 'number') {
        total += value;
        sawNumber = true;
      }
    }
    sums[column] = sawNumber ? total : null;
  }
  return sums;
};

export interface SortState {
  by: string;
  dir: 'asc' | 'desc';
}

interface ColumnFormat {
  numeric: boolean;
  kind: FormatKind;
  dateGranularity?: DateGranularity;
}

interface ResultsTableProps {
  columns: string[];
  rows: ResultRow[];
  columnLabels?: Map<string, string>;
  columnMeta?: Map<string, DatasetColumn>;
  dimFormats?: Map<string, DimensionFormat>;
  // Pivot column name (when the report pivots), so pivot value headers map
  // through the column's display formatter (claim_type → "Medicare Part A").
  pivotColumn?: string;
  truncated?: boolean;
  // When true AND `truncated` is showing its notice, the notice text is
  // extended to point at the chart's own full-window total (only meaningful
  // for a detail report with a valid chart config — see ReportBuilder).
  // Defaults to false/undefined, which renders today's notice unchanged.
  chartComputesFullWindow?: boolean;
  updating?: boolean;
  // Current sort from definition.sort: a multi-key ordered list mirroring the
  // definition's sort keys (precedence badges reflect that order). DuckDB has
  // already applied this ordering to `rows`; the table renders them as given and
  // never re-sorts in JavaScript.
  sort?: SortState[];
  // Result columns the user may sort by. A header outside this set (e.g. a
  // pivot-generated value column) is rendered as a plain, non-clickable label.
  sortableColumns?: string[];
  // Emitted on a sortable-header click with the clicked column key. A header
  // click reports one column; the parent cycles that column within the
  // multi-sort key list and recompiles/re-runs the query so DuckDB — never the
  // client — produces the new order.
  onSortChange?: (column: string) => void;
  // Emitted when a header is dragged onto a different header, with the source
  // and target column indices. Provided ONLY for the detail table (grouped/pivot
  // result columns are derived, so their order isn't user-reorderable). Its
  // presence toggles draggable headers. The parent persists the new order into
  // definition.columns and recompiles; headers then render in the new order.
  onColumnReorder?: (fromIndex: number, toIndex: number) => void;
  // The result columns that are group-by dimensions (definition.group_bys). When
  // provided (grouped/pivot views), the table (a) renders null dimension values
  // as "None" to match the chart, (b) blanks a repeated leading-dimension cell so
  // an AR aging bucket isn't reprinted on every row, and (c) appends a TOTAL row
  // summing the additive (count/sum) measure columns. Absent for detail dumps,
  // which keep the raw cell rendering.
  groupByColumns?: string[];
  // definition.subtotals: append a subtotal row after each leading-dimension
  // group, summing the same additive columns as the TOTAL row. Rendered only
  // when >= 2 group-bys, at least one additive column exists, AND the primary
  // sort keeps leading-dimension groups contiguous (empty sort or sorted by the
  // leading dimension first) — otherwise a run would fragment into duplicate
  // partial subtotals, so the table shows a hint instead.
  subtotals?: boolean;
}

const ResultsTable = ({
  columns,
  rows,
  columnLabels,
  columnMeta,
  dimFormats,
  pivotColumn,
  truncated,
  chartComputesFullWindow,
  updating,
  sort,
  sortableColumns,
  onSortChange,
  onColumnReorder,
  groupByColumns,
  subtotals,
}: ResultsTableProps) => {
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  // Source column index of an in-flight header drag. A ref (not state/
  // dataTransfer) because jsdom's synthetic drag events carry no dataTransfer,
  // and no re-render is needed while dragging.
  const dragSource = useRef<number | null>(null);

  // New results (a fresh rows array from re-running the definition) reset to
  // page 1 so the viewer never lands on a page that no longer exists — and so a
  // new sort lands on page 1, where the DuckDB-sorted extreme rows now sit.
  useEffect(() => {
    setPage(1);
  }, [rows]);

  const rowCount = rows.length;
  const totalPages = Math.max(1, Math.ceil(rowCount / pageSize));
  // Clamp defensively in case rows shrank before the reset effect ran.
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const visible = useMemo(
    () => rows.slice(start, start + pageSize),
    [rows, start, pageSize]
  );

  // Per-column formatting (alignment, money/number/date/text) resolved once and
  // keyed by column so header alignment and cell rendering stay in lockstep.
  const formats = useMemo(() => {
    const map: Record<string, ColumnFormat> = {};
    for (const column of columns) {
      const dim = dimFormats?.get(column);
      const kind = resolveFormatKind(column, columnMeta, dim);
      map[column] = {
        numeric: isNumericKind(kind),
        kind,
        dateGranularity: dim?.dateGranularity,
      };
    }
    return map;
  }, [columns, columnMeta, dimFormats]);

  const canSort = (column: string): boolean =>
    !!onSortChange && !!sortableColumns?.includes(column);

  const columnDefs = useMemo<ColumnDef<ResultRow>[]>(
    () =>
      columns.map(column => ({
        id: column,
        accessorFn: (row: ResultRow) => row[column],
        header: humanizeHeader(
          column,
          columnLabels,
          columnMeta,
          pivotColumn ? { column: pivotColumn } : undefined
        ),
        enableSorting: canSort(column),
      })),
    [
      columns,
      columnLabels,
      columnMeta,
      pivotColumn,
      sortableColumns,
      onSortChange,
    ]
  );

  // Controlled sorting state mirrors definition.sort. With manualSorting the
  // table never reorders rows itself — it only reflects the active sort in the
  // header indicators.
  const sorting = useMemo<SortingState>(
    () => (sort ?? []).map(s => ({ id: s.by, desc: s.dir === 'desc' })),
    [sort]
  );

  // Precedence rank (1-based) of each column within an active multi-sort, so a
  // header can show a small "1"/"2" badge that reads identically to the Sort
  // sidebar's row order. Only populated when more than one sort key is active —
  // a single sort is unambiguous from the arrow alone.
  const sortPrecedence = useMemo(() => {
    const map = new Map<string, number>();
    if ((sort?.length ?? 0) > 1) {
      sort!.forEach((s, i) => map.set(s.by, i + 1));
    }
    return map;
  }, [sort]);

  // Group-by dimension columns get "None" for nulls (matching the chart) and the
  // leading one is merged down runs of equal values.
  const dimensionSet = useMemo(
    () => new Set(groupByColumns ?? []),
    [groupByColumns]
  );
  const leadingDimension = groupByColumns?.[0];

  // Bottom TOTAL row for grouped/pivot views: sum the additive (count / sum)
  // measure columns across ALL rows (not just the visible page) so the number the
  // user came for — total outstanding balance, total claim count — is present.
  // Non-additive measures (avg/min/max) and dimension columns are left blank
  // because summing them is meaningless.
  const totals = useMemo(() => {
    if (!groupByColumns || groupByColumns.length === 0) return null;
    return sumAdditiveColumns(rows, columns, dimensionSet);
  }, [groupByColumns, columns, dimensionSet, rows]);

  // Subtotal eligibility is split from sort compatibility so an enabled-but-
  // sort-suppressed state can explain itself (hint below) instead of rows
  // silently disappearing when the user sorts by a measure.
  const hasAdditiveColumns = useMemo(
    () =>
      columns.some(
        column => !dimensionSet.has(column) && seriesKeysAdditive([column])
      ),
    [columns, dimensionSet]
  );
  const subtotalsEligible =
    !!subtotals && (groupByColumns?.length ?? 0) >= 2 && hasAdditiveColumns;
  // Groups stay contiguous when there is no sort (the compiler defaults to
  // ordering by the group-by dimensions) or when the PRIMARY sort key is the
  // leading dimension (direction and later keys can't split a run).
  const subtotalSortCompatible =
    !sort?.length || sort[0].by === leadingDimension;
  const showSubtotals = subtotalsEligible && subtotalSortCompatible;

  // Absolute index of each leading-dimension run's LAST row → that run's sums,
  // computed over ALL rows (not the visible page) so a group spanning a page
  // break subtotals once, on the page holding its final row.
  const subtotalAt = useMemo(() => {
    if (!showSubtotals || !leadingDimension || rows.length === 0) return null;
    const map = new Map<number, Record<string, number | null>>();
    let runStart = 0;
    for (let i = 1; i <= rows.length; i++) {
      if (
        i === rows.length ||
        rows[i][leadingDimension] !== rows[i - 1][leadingDimension]
      ) {
        map.set(
          i - 1,
          sumAdditiveColumns(rows.slice(runStart, i), columns, dimensionSet)
        );
        runStart = i;
      }
    }
    return map;
  }, [showSubtotals, leadingDimension, rows, columns, dimensionSet]);

  const table = useReactTable<ResultRow>({
    data: visible,
    columns: columnDefs,
    state: { sorting },
    manualSorting: true,
    // DuckDB owns ordering; header clicks route through onSortChange, so the
    // table's own sorting state is read-only here.
    onSortingChange: () => {},
    // Stable identity by ABSOLUTE row index: subtotal rows render as siblings
    // keyed `subtotal:<index>`, so data-row ids must not collide across pages.
    getRowId: (_row, index) => `data:${start + index}`,
    getCoreRowModel: getCoreRowModel(),
  });

  const countLabel =
    rowCount === 1 ? '1 row' : `${rowCount.toLocaleString()} rows`;
  // Absolute row range for the current page ("Rows 51–100 of 3,842") so a paged
  // result says where you are, not just the page ordinal. Only meaningful once
  // the result spans more than one page.
  const rangeLabel = `Rows ${(start + 1).toLocaleString()}–${(
    start + visible.length
  ).toLocaleString()} of ${rowCount.toLocaleString()}`;
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;
  // Collapse the pager only when the result fits the smallest page size — never
  // based on totalPages, which depends on the current (possibly larger) page
  // size. Keying off the minimum size keeps the rows-per-page selector reachable
  // so a user who bumped to 250 can return to 50/100 (totalPages === 1 at 250
  // would otherwise strand them). Prev/Next are still disabled on a single page.
  const showPager = rowCount > PAGE_SIZE_OPTIONS[0];

  const goPrev = () => {
    if (!isFirstPage) setPage(p => p - 1);
  };
  const goNext = () => {
    if (!isLastPage) setPage(p => p + 1);
  };

  return (
    <div className='space-y-2'>
      {truncated && (
        <p className='text-xs text-amber-600 dark:text-amber-500'>
          {chartComputesFullWindow ? (
            <>
              Table shows the first {DETAIL_ROW_LIMIT.toLocaleString()} rows;
              the chart is calculated from every matching claim in this window.
            </>
          ) : (
            <>
              This report hit the {DETAIL_ROW_LIMIT.toLocaleString()}-row limit.
              Narrow the date range or filters to see everything; exports
              include only these rows.
            </>
          )}
        </p>
      )}
      {/* role=status so a sort change that suppresses subtotals announces why
          the rows vanished instead of them silently disappearing. */}
      {subtotalsEligible && !subtotalSortCompatible && (
        <p role='status' className='text-xs text-muted-foreground'>
          Subtotals are hidden while results are sorted by{' '}
          {humanizeHeader(sort![0].by, columnLabels, columnMeta)}. Make{' '}
          {humanizeHeader(leadingDimension!, columnLabels, columnMeta)} the
          first sort to show them.
        </p>
      )}
      {/* Bounded viewport: the table scrolls inside a fixed max-height with a
          sticky header, so a 100/250-row page never grows the whole page and
          scrolls the header (and the Table/Chart toggle) out of view. Row height
          stays dense — the viewport bounds the height, padding is not added. */}
      <div className='max-h-[calc(100vh-18rem)] overflow-auto rounded-md border'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const numeric = formats[header.column.id]?.numeric ?? false;
                  const sortable = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const label = header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      );
                  const ariaSort = !sortable
                    ? undefined
                    : sorted === 'asc'
                      ? 'ascending'
                      : sorted === 'desc'
                        ? 'descending'
                        : 'none';
                  // Column reorder is a detail-table-only affordance (parent
                  // passes onColumnReorder there); the drag source/target are the
                  // header's index in the current column order. Suppressed while
                  // results are revalidating so a reorder can't race stale columns.
                  const draggable = !!onColumnReorder && !updating;
                  const colIndex = columns.indexOf(header.column.id);
                  const precedence = sortPrecedence.get(header.column.id);
                  return (
                    <TableHead
                      key={header.id}
                      scope='col'
                      aria-sort={ariaSort}
                      draggable={draggable}
                      onDragStart={
                        draggable
                          ? () => {
                              dragSource.current = colIndex;
                            }
                          : undefined
                      }
                      onDragOver={
                        draggable
                          ? e => {
                              // Allow a drop only while a header drag is active.
                              if (dragSource.current !== null)
                                e.preventDefault();
                            }
                          : undefined
                      }
                      onDrop={
                        draggable
                          ? () => {
                              const from = dragSource.current;
                              dragSource.current = null;
                              if (from !== null && from !== colIndex) {
                                onColumnReorder?.(from, colIndex);
                              }
                            }
                          : undefined
                      }
                      onDragEnd={
                        draggable
                          ? () => {
                              // Cleanup only (never emits): a cancelled or
                              // dropped-outside drag must not leave a stale
                              // source index for a later dragOver/drop to misread.
                              dragSource.current = null;
                            }
                          : undefined
                      }
                      className={`sticky top-0 z-10 bg-background ${
                        draggable ? 'group/head cursor-move' : ''
                      } ${numeric ? 'text-right' : ''}`.trim()}
                    >
                      <span
                        className={`inline-flex items-center gap-1 ${
                          numeric ? 'flex-row-reverse' : ''
                        }`}
                      >
                        {draggable && (
                          <GripVertical className='h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/head:opacity-40' />
                        )}
                        {sortable ? (
                          <button
                            type='button'
                            className={`group inline-flex items-center gap-1 hover:text-foreground ${
                              numeric ? 'flex-row-reverse' : ''
                            }`}
                            onClick={() => onSortChange?.(header.column.id)}
                          >
                            {label}
                            {sorted === 'asc' ? (
                              <ArrowUp className='h-3 w-3' />
                            ) : sorted === 'desc' ? (
                              <ArrowDown className='h-3 w-3' />
                            ) : (
                              <ChevronsUpDown className='h-3 w-3 opacity-40 group-hover:opacity-70' />
                            )}
                            {precedence !== undefined && (
                              <span
                                className='flex h-3.5 min-w-3.5 items-center justify-center rounded-sm bg-muted px-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground'
                                aria-label={`Sort priority ${precedence}`}
                              >
                                {precedence}
                              </span>
                            )}
                          </button>
                        ) : (
                          label
                        )}
                      </span>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row, rowIndex) => {
              // Subtotal row for the group ENDING at this data row, if any.
              // Rendered as a sibling <tr> (never inserted into the table's
              // data array) so pagination, row counts, the merged-leading-cell
              // comparison, and the grand total all keep operating on real
              // result rows only.
              const subtotalSums = subtotalAt?.get(start + rowIndex);
              const dataRow = (
                <TableRow
                  key={row.id}
                  // Dense scan affordances: a hover highlight plus low-contrast
                  // zebra so tracking a value across a wide row is easier. Styling
                  // only — the row order is still exactly what DuckDB returned.
                  // Zebra parity comes from the data-row index, not CSS :nth-child,
                  // so an injected subtotal <tr> can't flip the striping under the
                  // rows that follow it.
                  className={`transition-colors hover:bg-muted/50 ${
                    rowIndex % 2 === 1 ? 'bg-muted/20' : ''
                  }`.trim()}
                >
                  {row.getVisibleCells().map(cell => {
                    const columnId = cell.column.id;
                    const fmt = formats[columnId];
                    const isDimension = dimensionSet.has(columnId);
                    const value = cell.getValue();
                    // Blank a repeated leading-dimension cell so an aging bucket
                    // ("0-30") isn't reprinted on every row of its run — the value
                    // shows once, like Sigma/Omni. Compared against the previous
                    // VISIBLE row so paging never hides the first value of a page.
                    const mergedAway =
                      columnId === leadingDimension &&
                      rowIndex > 0 &&
                      visible[rowIndex]?.[columnId] ===
                        visible[rowIndex - 1]?.[columnId];
                    return (
                      <TableCell
                        key={cell.id}
                        className={`py-1.5 ${
                          fmt?.numeric ? 'text-right tabular-nums' : ''
                        }`.trim()}
                      >
                        {mergedAway
                          ? ''
                          : formatValue(value, fmt?.kind ?? 'text', {
                              dateGranularity: fmt?.dateGranularity,
                              // Null dimension values read as "None" (matching the
                              // chart); measures keep the em-dash placeholder.
                              emptyText: isDimension ? 'None' : undefined,
                            })}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
              if (!subtotalSums) return dataRow;
              return (
                <Fragment key={row.id}>
                  {dataRow}
                  <TableRow
                    // A ruled row, not another shaded band: shading would
                    // collide with the identical zebra tint on data rows
                    // (Chris's feedback — subtotals were indistinguishable),
                    // so the subtotal stays on the plain background with
                    // top/bottom rules and semibold numerals. Shading remains
                    // the grand Total's (bg-muted/40) distinguishing
                    // treatment, keeping the subtotal < total hierarchy. No
                    // hover tint — structural row, not a data row.
                    className='border-y border-border bg-background font-medium'
                  >
                    {columns.map(column => {
                      const fmt = formats[column];
                      const sum = subtotalSums[column];
                      return (
                        <TableCell
                          key={column}
                          className={`py-1.5 ${
                            fmt?.numeric
                              ? 'text-right font-semibold tabular-nums'
                              : ''
                          }`.trim()}
                        >
                          {column === leadingDimension
                            ? // "subtotal" (not "total") so it can't be read as
                              // the grand Total row; the group value goes
                              // through the dimension's own formatter so date/
                              // bucket groups and null → "None" match the cells
                              // above it.
                              `${formatValue(
                                rows[start + rowIndex]?.[column],
                                fmt?.kind ?? 'text',
                                {
                                  dateGranularity: fmt?.dateGranularity,
                                  emptyText: 'None',
                                }
                              )} subtotal`
                            : sum === null
                              ? ''
                              : formatValue(sum, fmt?.kind ?? 'number')}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </Fragment>
              );
            })}
            {totals && (
              <TableRow className='border-t-2 border-border bg-muted/40 font-semibold'>
                {columns.map((column, i) => {
                  const fmt = formats[column];
                  const sum = totals[column];
                  return (
                    <TableCell
                      key={column}
                      className={`py-1.5 ${
                        fmt?.numeric ? 'text-right tabular-nums' : ''
                      }`.trim()}
                    >
                      {i === 0
                        ? 'Total'
                        : sum === null
                          ? ''
                          : formatValue(sum, fmt?.kind ?? 'number')}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <p
          className='flex items-center gap-2 text-xs text-muted-foreground'
          aria-live='polite'
        >
          <span>{countLabel}</span>
          {showPager && <span aria-hidden>·</span>}
          {showPager && <span>{rangeLabel}</span>}
          {updating && <span>Updating results…</span>}
        </p>
        {showPager && (
          <div className='flex items-center gap-4'>
            <div className='flex items-center gap-2'>
              <span
                id='rows-per-page-label'
                className='text-xs text-muted-foreground'
              >
                Rows per page
              </span>
              <Select
                value={String(pageSize)}
                onValueChange={value => {
                  setPageSize(Number(value));
                  setPage(1);
                }}
              >
                <SelectTrigger
                  selectSize='sm'
                  className='w-[80px]'
                  aria-labelledby='rows-per-page-label'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='flex items-center gap-1'>
              <Button
                variant='ghost'
                size='sm'
                aria-label='Go to previous page'
                disabled={isFirstPage}
                onClick={goPrev}
              >
                <ChevronLeft className='h-4 w-4' />
                Previous
              </Button>
              <span className='px-2 text-xs text-muted-foreground'>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant='ghost'
                size='sm'
                aria-label='Go to next page'
                disabled={isLastPage}
                onClick={goNext}
              >
                Next
                <ChevronRight className='h-4 w-4' />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsTable;
